import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Asset, AssetUpload } from "src/types/assets";

import styles from "./background.module.scss";
import { useSelector } from "react-redux";
import _ from "lodash";
import { BackgroundUploadTask } from "src/types/backgroundTask";
import pluralize from "pluralize";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { completeBackgroundUploadTask, invalidateBackgroundUploadTask } from "src/redux/actions/backgroundTask";
import FileUploadService from "src/services/file.upload.service";
import { invalidateAssetUpload, toggleAssetStatusInContentSimilarityQueue } from "src/redux/actions/assets";

export const BackgroundUploadTaskManager: React.FC<object> = () => {
  const [tasks, setTasks] = useState([]);

  const isStorageLimitReached = useSelector((state:any) => state.asset.storageLimit.isLimitReached)

  const newTasks = useSelector((state: any) => {
    const taskList = _.get(state, "backgroundTasks.tasks", []);

    const newTasks = taskList.filter((task: any) => {
      return !_.find(tasks, task);
    });

    return newTasks;

  }, _.isEqual);

  useEffect(() => {
    if (!isStorageLimitReached && _.size(newTasks) > 0) {
      newTasks.forEach((task: any) => {
        toast(<BackgroundUploadTaskView task={task} />, {
          toastId: task.id,
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          position: "bottom-right",
        });
      });

      let updatedTasks = _.union(tasks, newTasks);
      updatedTasks = _.uniqBy(updatedTasks, "id");
      setTasks(updatedTasks);
    }
  }, [newTasks]);

  return null;
}

interface BackgroundUploadTaskProps {
  task: BackgroundUploadTask;
}

export const BackgroundUploadTaskView: React.FC<BackgroundUploadTaskProps> = ({ task }) => {
  const dispatch = useDispatch();

  const updatedTask = useSelector((state: any) => {
    const tasks = _.get(state, "backgroundTasks.tasks", []);
    const currentTask = _.find(tasks, { id: task.id });
    return currentTask;
  }, _.isEqual);

  const assets = task.assets as (Asset | AssetUpload)[];
  const name = _.get(task, "entity.name");
  const url = _.get(task, "entity.url") || '#';

  const progress = useSelector(reduxState => {
    const uploadState = _.get(reduxState, 'asset.upload');
    const items = _.keys(uploadState).map(key => {
      const upload = _.get(uploadState, `[${key}]`);
      return upload;
    });

    const totalProgress = assets.reduce((total, item) => {
      const uploadItem = items.filter((asset: any) => {
        const created = _.get(asset, 'created');
        const entityType = _.get(asset, 'item.entityType');
        return entityType === 'asset-upload' && !created;
      }).find((asset: any) => asset.id === item.id);

      if (uploadItem) {
        const progress = _.get(uploadItem, 'item.progress') || 0;
        return total + progress;
      } else {
        return total + 100;
      }
    }, 0);

    return totalProgress / _.size(assets);
  }, _.isEqual);

  const uploading = useSelector(reduxState => {
    const uploadState = _.get(reduxState, 'asset.upload');
    const items = _.keys(uploadState).map(key => {
      const upload = _.get(uploadState, `[${key}]`);
      return upload;
    });

    const uploadingItems = items.filter((asset: any) => {
      const created = _.get(asset, 'created');
      return !created;
    });

    return !_.isEmpty(uploadingItems);
  }, _.isEqual);

  const completion = useSelector(reduxState => {
    const tasks = _.get(reduxState, 'backgroundTasks.tasks') || []; 
    const currentTask = tasks.find((item: any) => item.id === task.id);

    return !currentTask ? false : _.get(currentTask, 'completed');
  }, _.isEqual);

  const synced = useSelector(reduxState => {
    const tasks = _.get(reduxState, 'backgroundTasks.tasks') || []; 
    const currentTask = tasks.find((item: any) => item.id === task.id);
    return !currentTask ? false : _.some((currentTask as BackgroundUploadTask).assets, (asset: any) => _.get(asset, 'synced') === true);
  }, _.isEqual);

  useEffect(() => {
    if (!uploading) {
      dispatch(completeBackgroundUploadTask(task.id));
    }
  }, [uploading]);

  useEffect(() => {
    if (completion) {
      _.map(updatedTask.assets, (item) => {
        dispatch(toggleAssetStatusInContentSimilarityQueue({asset: item.asset, initialise: true}));
      });
      toast.dismiss(task.id);
      dispatch(invalidateBackgroundUploadTask(task.id));
    }
  }, [completion]);

  useEffect(() => {
    if (synced) {
      _.map(updatedTask.assets, (item) => {
        if (_.get(item, 'synced')) {
          dispatch(toggleAssetStatusInContentSimilarityQueue({ asset: item.asset, initialise: true }));
        }
      });
      toast.dismiss(task.id);
      dispatch(invalidateBackgroundUploadTask(task.id));
    }
  }, [synced]);

  const handleClose = () => {
    assets.forEach((asset) => {
      const entityType = _.get(asset, "entityType");
      if (entityType === "asset-upload") {
        const uploader = FileUploadService.getUploader(asset.id);
        if (uploader) {
          uploader.abortUpload();
          dispatch(invalidateAssetUpload(asset.id));
        }
      }
    });

    dispatch(invalidateBackgroundUploadTask(task.id));
    toast.dismiss(task.id);
  }
  
  return (
    <div className={styles.task}>
      <div className={styles.message}>
        uploading <em>{_.size(assets)}</em> {pluralize('assets', _.size(assets))} to <Link to={url}>{name}</Link>
      </div>
      <button className={styles.close} onClick={handleClose}>
        <i className="fa-regular fa-circle-xmark"></i>
      </button>
      {uploading &&
        <div className={styles.progress}>
          <div className={styles.bar} style={{ width: `${progress}%` }}></div>
        </div>
      }
    </div>
  );
}